import { HttpTypes } from "@medusajs/types"
import { isVtoReadyVariant } from "@modules/vto/lib/product"

import {
  ImmersiveShopCategory,
  ImmersiveShopOption,
  ImmersiveShopPrice,
  ImmersiveShopProduct,
  ImmersiveShopVariant,
} from "../types"

function isVariantAvailable(variant: HttpTypes.StoreProductVariant) {
  if (!variant.manage_inventory || variant.allow_backorder) {
    return true
  }

  return (variant.inventory_quantity ?? 0) > 0
}

function formatPrice(amount: number, currencyCode: string) {
  try {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: currencyCode,
    }).format(amount)
  } catch {
    return `${amount.toFixed(2)} ${currencyCode.toUpperCase()}`
  }
}

function adaptPrice(
  variant: HttpTypes.StoreProductVariant | undefined
): ImmersiveShopPrice | null {
  const calculatedPrice = variant?.calculated_price
  const amount = calculatedPrice?.calculated_amount
  const currencyCode = calculatedPrice?.currency_code

  if (typeof amount !== "number" || !currencyCode) {
    return null
  }

  return {
    amount,
    currencyCode,
    formatted: formatPrice(amount, currencyCode),
  }
}

function adaptVariant(
  variant: HttpTypes.StoreProductVariant
): ImmersiveShopVariant {
  const optionValues: Record<string, string> = {}

  ;(variant.options ?? []).forEach((optionValue) => {
    if (optionValue.option_id) {
      optionValues[optionValue.option_id] = optionValue.value
    }
  })

  return {
    id: variant.id,
    title: variant.title ?? "",
    sku: variant.sku ?? null,
    available: isVariantAvailable(variant),
    vtoReady: isVtoReadyVariant(variant),
    optionValues,
    price: adaptPrice(variant),
  }
}

function adaptOptions(product: HttpTypes.StoreProduct): ImmersiveShopOption[] {
  return (product.options ?? []).map((option) => ({
    id: option.id,
    title: option.title,
    values: (option.values ?? []).map((value) => value.value),
  }))
}

export function adaptImmersiveShopData(
  products: HttpTypes.StoreProduct[],
  categories: HttpTypes.StoreProductCategory[]
): {
  products: ImmersiveShopProduct[]
  categories: ImmersiveShopCategory[]
} {
  const productIdsByCategory = new Map<string, string[]>()

  products.forEach((product) => {
    ;(product.categories ?? []).forEach((category) => {
      const productIds = productIdsByCategory.get(category.id) ?? []
      productIds.push(product.id)
      productIdsByCategory.set(category.id, productIds)
    })
  })

  const normalizedCategories = categories
    .map((category) => {
      const productIds = [
        ...(category.products ?? [])
          .map((product) => product.id)
          .filter((id): id is string => Boolean(id)),
        ...(productIdsByCategory.get(category.id) ?? []),
      ]

      return {
        id: category.id,
        name: category.name,
        handle: category.handle,
        productIds: Array.from(new Set(productIds)),
      }
    })
    .filter((category) => category.productIds.length > 0)

  const categoryIdsByProduct = new Map<string, string[]>()

  normalizedCategories.forEach((category) => {
    category.productIds.forEach((productId) => {
      const categoryIds = categoryIdsByProduct.get(productId) ?? []
      categoryIds.push(category.id)
      categoryIdsByProduct.set(productId, categoryIds)
    })
  })

  const normalizedProducts = products.map((product) => {
    const variants = (product.variants ?? []).map(adaptVariant)
    const defaultVariant =
      variants.find((candidate) => candidate.available) ?? variants[0]
    const imageUrls = [
      ...(product.images ?? []).map((image) => image.url),
      product.thumbnail,
    ].filter(
      (url, index, urls): url is string =>
        Boolean(url) && urls.indexOf(url) === index
    )

    return {
      id: product.id,
      title: product.title,
      handle: product.handle,
      description: product.description ?? "",
      imageUrls,
      defaultVariantId: defaultVariant?.id ?? null,
      available: variants.some((variant) => variant.available),
      price: defaultVariant?.price ?? null,
      categoryIds: categoryIdsByProduct.get(product.id) ?? [],
      variants,
      options: adaptOptions(product),
    } satisfies ImmersiveShopProduct
  })

  return {
    products: normalizedProducts,
    categories: normalizedCategories,
  }
}
